/**
 * sl-sbux.8.5: CustomHtmlBlock ViewComponent.
 *
 * Public rendering only injects HTML that was already rendered and sanitized by
 * MaterializationService. Builder/preview show a stable placeholder until the
 * next materialized snapshot carries `htmlBlocksRendered`.
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type { ViewComponent } from '../../../types/view-component.types';
import { PlaceholderWarning } from '../../app-base/placeholder-warning';

const configSchema = z.object({
  htmlBlockId: z.string().default(''),
});

export const CustomHtmlBlockComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.CUSTOM_HTML_BLOCK,
  type: 'content',
  name: 'Custom HTML Block',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    '<>'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    htmlBlockId: '',
  },
  render({ config, runtimeContext }) {
    const typedConfig = config as z.infer<typeof configSchema>;
    const routeKey =
      runtimeContext?.page &&
      typeof runtimeContext.page['routeKey'] === 'string'
        ? runtimeContext.page['routeKey']
        : null;
    const html =
      routeKey && runtimeContext?.componentInstanceId
        ? runtimeContext?.htmlBlocksRendered?.[routeKey]?.[
            runtimeContext.componentInstanceId
          ]
        : null;

    if (!html) {
      return createElement(PlaceholderWarning, {
        reasonKey: 'bodyMissing',
        role: 'html',
      });
    }

    return createElement('div', {
      'data-sl-html-block-id': typedConfig.htmlBlockId,
      dangerouslySetInnerHTML: { __html: html },
    });
  },
  palettePreview() {
    return createElement(
      'div',
      {
        style: {
          padding: 'var(--sl-app-padding-sm)',
          fontSize: 'var(--sl-app-font-size-xs)',
          color: 'var(--streamlain-v2-text-secondary)',
        },
      },
      '<> HTML'
    );
  },
};
